import {
  getCurrencyExponent,
  type SupportedCurrencyCode,
} from "@repo/api-schema";

/**
 * Parse a user-entered major-unit amount ("12.34") into integer minor units
 * (cents/pence) for the given currency exponent, rounding half-up on the digit
 * after the cut. Returns `NaN` for empty or non-numeric input. Kept BigInt-free
 * (amounts here are far below `Number.MAX_SAFE_INTEGER`) and standalone so the
 * settlement form doesn't depend on the heavy expense form's internals.
 */
export function amountToMinor(
  amount: string,
  currency: SupportedCurrencyCode,
): number {
  const exponent = getCurrencyExponent(currency);
  const trimmed = amount.trim();
  if (trimmed === "") return Number.NaN;
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    const major = Number(trimmed);
    if (!Number.isFinite(major)) return Number.NaN;
    return Math.round(major * 10 ** exponent);
  }
  const negative = trimmed.startsWith("-");
  const digits = negative ? trimmed.slice(1) : trimmed;
  const [intPart, fracPart = ""] = digits.split(".");
  const padded = (fracPart + "0".repeat(exponent)).slice(0, exponent);
  const cutDigit = (fracPart[exponent] ?? "0").charCodeAt(0) - 48;
  const base = Number((intPart ?? "0") + padded);
  if (!Number.isFinite(base)) return Number.NaN;
  const rounded = base + (cutDigit >= 5 ? 1 : 0);
  return negative ? -rounded : rounded;
}

/** The `<input type="number">` step for a currency (e.g. "0.01", or "1" for JPY). */
export function minorStepFor(currency: SupportedCurrencyCode): string {
  const exponent = getCurrencyExponent(currency);
  return exponent === 0 ? "1" : `0.${"0".repeat(exponent - 1)}1`;
}
